/**
 * Plugin manifest registration system.
 * Runtime registry — plugins register on app init; core never imports plugin code.
 */

import { Todo, WebRoute } from '../types/domain';
import {
  CompletionGuard,
  InboxSectionContribution,
  IPluginRegistry,
  PluginRegistration,
  TodoDetailSlot,
  TodoDetailSlotRenderer,
  TodoRenderer,
} from './types';

// localStorage key for the last-known enabled state, keyed per-plugin-id.
// Hydrated on registry construction so a hard refresh starts at server truth
// instead of enabled_by_default. Without this, plugins the user disabled
// reappear between app boot and fetchPluginsSaga completing.
const CACHE_KEY = 'plugin_enabled_state';

function readCache(): Record<string, boolean> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_KEY) : null;
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeCache(state: Record<string, boolean>): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CACHE_KEY, JSON.stringify(state));
    }
  } catch {
    /* quota / private mode — ignore */
  }
}

export class PluginRegistry implements IPluginRegistry {
  private plugins: Map<string, PluginRegistration> = new Map();
  private enabledSet: Set<string> = new Set();
  private renderers: Map<string, TodoRenderer> = new Map();
  private cachedState: Record<string, boolean> = readCache();

  register(registration: PluginRegistration): void {
    this.plugins.set(registration.id, registration);

    // Prefer last-known server state (from localStorage); fall back to
    // enabled_by_default for plugins we've never seen (fresh install, new
    // plugin shipped in this deploy).
    const cached = this.cachedState[registration.id];
    const initialEnabled = cached !== undefined ? cached : registration.enabled_by_default;
    if (initialEnabled) {
      this.enabledSet.add(registration.id);
    }

    // Register todo renderers by type_tag
    if (registration.todoRenderers) {
      for (const [typeTag, renderer] of Object.entries(registration.todoRenderers)) {
        this.renderers.set(typeTag, renderer);
      }
    }
  }

  getTodoRenderer(typeTag: string): TodoRenderer | undefined {
    // Only return renderers for enabled plugins
    const plugin = this.findPluginForTypeTag(typeTag);
    if (plugin && !this.enabledSet.has(plugin.id)) {
      return undefined;
    }
    return this.renderers.get(typeTag);
  }

  getIconForTypeTag(typeTag: string): string | undefined {
    const plugin = this.findPluginForTypeTag(typeTag);
    if (!plugin || !this.enabledSet.has(plugin.id)) return undefined;
    const tt = plugin.todo_types.find((t) => t.type_tag === typeTag);
    // Prefer the type-specific glyph so two types in one plugin can differ
    // (commitments.mine vs commitments.followup); fall back to the plugin icon.
    return tt?.icon ?? plugin.icon;
  }

  getDetailSlotRenderers(slot: TodoDetailSlot): TodoDetailSlotRenderer[] {
    const out: TodoDetailSlotRenderer[] = [];
    for (const plugin of this.plugins.values()) {
      if (!this.enabledSet.has(plugin.id)) continue;
      const renderer = plugin.detailSlots?.[slot];
      if (renderer) out.push(renderer);
    }
    return out;
  }

  getCompletionGuards(): CompletionGuard[] {
    const out: CompletionGuard[] = [];
    for (const plugin of this.plugins.values()) {
      if (!this.enabledSet.has(plugin.id)) continue;
      if (plugin.completionGuard) out.push(plugin.completionGuard);
    }
    return out;
  }

  claimsInboxTodo(todo: Todo): boolean {
    for (const plugin of this.plugins.values()) {
      if (!this.enabledSet.has(plugin.id)) continue;
      if (plugin.inboxClaim?.(todo)) return true;
    }
    return false;
  }

  getInboxSections(): InboxSectionContribution[] {
    const out: InboxSectionContribution[] = [];
    for (const plugin of this.plugins.values()) {
      if (!this.enabledSet.has(plugin.id)) continue;
      if (plugin.inboxSections) out.push(...plugin.inboxSections);
    }
    return out.sort((a, b) => a.order - b.order);
  }

  getEnabledPlugins(): PluginRegistration[] {
    return Array.from(this.plugins.values()).filter((p) =>
      this.enabledSet.has(p.id)
    );
  }

  getWebRoutes(): WebRoute[] {
    return this.getEnabledPlugins()
      .flatMap((p) => p.web_routes)
      .sort((a, b) => a.sidebar_order - b.sidebar_order);
  }

  getPlugin(id: string): PluginRegistration | undefined {
    return this.plugins.get(id);
  }

  setEnabled(pluginId: string, enabled: boolean): void {
    if (enabled) {
      this.enabledSet.add(pluginId);
    } else {
      this.enabledSet.delete(pluginId);
    }
    this.cachedState[pluginId] = enabled;
    writeCache(this.cachedState);
  }

  isEnabled(pluginId: string): boolean {
    return this.enabledSet.has(pluginId);
  }

  private findPluginForTypeTag(typeTag: string): PluginRegistration | undefined {
    for (const plugin of this.plugins.values()) {
      if (plugin.todo_types.some((t) => t.type_tag === typeTag)) {
        return plugin;
      }
    }
    return undefined;
  }
}

// Singleton instance
export const pluginRegistry = new PluginRegistry();
